import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Checkbox from 'src/components/ui/Checkbox/Checkbox';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import LoadingSkeleton from 'src/components/ui/LoadingSkeleton/LoadingSkeleton';
import API_BASE_URL from 'src/constants';
import logger from 'src/utils/logger';
import { triggerBlobDownload } from 'src/utils/downloadUtils';
import { Term } from './schema';
import { extractBackendErrorMessage, parseFetchError } from './errorUtils';

interface TermsListProps {
  registrationKey: string;
  terms: Term[];
  isLoading: boolean;
  acceptedTermIds: number[];
  onToggle: (termId: number, accepted: boolean) => void;
}

const downloadTermPdf = async (registrationKey: string, termId: number): Promise<Blob> => {
  const response = await fetch(
    `${API_BASE_URL}portal/registrations/${encodeURIComponent(registrationKey)}/terms/${termId}`,
    {
      method: 'GET',
      headers: { Accept: 'application/pdf' },
    },
  );
  if (!response.ok) {
    throw await parseFetchError(response);
  }
  return response.blob();
};

const TermsList: React.FC<TermsListProps> = ({ registrationKey, terms, isLoading, acceptedTermIds, onToggle }) => {
  const { t } = useTranslation();
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const handleDownload = async (term: Term) => {
    if (term.id == null) return;
    setDownloadingId(term.id);
    try {
      const blob = await downloadTermPdf(registrationKey, term.id);
      const filename = `${term.name || 'term'}.pdf`;
      triggerBlobDownload(blob, filename);
    } catch (err) {
      logger.error('Term download failed', err);
      const backendMessage = extractBackendErrorMessage(err);
      toast.error(backendMessage || t('registration.terms.download_failed'));
    } finally {
      setDownloadingId(null);
    }
  };

  if (isLoading) {
    return <LoadingSkeleton numberOfItems={3} />;
  }

  if (terms.length === 0) {
    return <p className="typo-text-14 text-faded italic">{t('registration.terms.empty')}</p>;
  }

  return (
    <ul className="flex flex-col gap-8">
      {terms.map(term => {
        const { id } = term;
        if (id == null) return null;
        const checked = acceptedTermIds.includes(id);
        return (
          <li key={id} className="flex items-center justify-between gap-12 px-12 py-12 border border-subtle rounded-8">
            <div className="flex items-center gap-12 min-w-0">
              <Checkbox checked={checked} onChange={e => onToggle(id, e.target.checked)} />
              <div className="min-w-0">
                <p className="typo-text-14--bold text-full truncate">{term.name}</p>
                {(term.type_of_terms || term.valid_from) && (
                  <p className="typo-text-12 text-faded">
                    {term.type_of_terms ? `${term.type_of_terms}` : ''}
                    {term.type_of_terms && term.valid_from ? ' · ' : ''}
                    {term.valid_from ? `${t('registration.terms.valid_from')} ${term.valid_from}` : ''}
                  </p>
                )}
              </div>
            </div>
            <Button
              title={t('registration.terms.download')}
              tone={ButtonToneProps.GHOST}
              icon="download"
              onClick={() => handleDownload(term)}
              disabled={downloadingId === id}
            />
          </li>
        );
      })}
    </ul>
  );
};

export default TermsList;
